import { BUILD_INFO } from '@/buildInfo';

const STORAGE_KEY = 'tuvion.staleChunkReloadAt';
const WINDOW_MS = 5 * 60 * 1000;

type ReloadTarget = Pick<Window, 'addEventListener' | 'sessionStorage' | 'fetch'> & {
  location: Pick<Location, 'reload'>;
};

/** True at most once per window, so a release whose chunks are really missing cannot reload in a loop. */
export function claimStaleChunkReload(
  storage: Pick<Storage, 'getItem' | 'setItem'>,
  now: number = Date.now(),
): boolean {
  try {
    const last = Number(storage.getItem(STORAGE_KEY));
    if (last > 0 && now >= last && now - last < WINDOW_MS) return false;
    storage.setItem(STORAGE_KEY, String(now));
    return true;
  } catch {
    return false;
  }
}

/** False unless the site answers with another build: a tab that is only offline must not be reloaded into an error page. */
export async function releaseHasChanged(
  fetchVersion: typeof fetch,
  current: Pick<typeof BUILD_INFO, 'sha' | 'builtAt'> = BUILD_INFO,
): Promise<boolean> {
  try {
    const response = await fetchVersion('/version.json', { cache: 'no-store' });
    if (!response.ok) return false;
    const { sha, built_at: builtAt } = await response.json();
    if (typeof sha !== 'string' || sha === '') return false;
    return sha !== current.sha || (typeof builtAt === 'string' && builtAt !== current.builtAt);
  } catch {
    return false;
  }
}

function storageOf(target: ReloadTarget): Storage | null {
  try {
    return target.sessionStorage;
  } catch {
    return null;
  }
}

export function installStaleChunkReload(target: ReloadTarget = window): void {
  target.addEventListener('vite:preloadError', () => {
    void releaseHasChanged((input, init) => target.fetch(input, init)).then((changed) => {
      const storage = storageOf(target);
      if (changed && storage && claimStaleChunkReload(storage)) target.location.reload();
    });
  });
}
